import { useEffect, useMemo, useState } from 'react';
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { GripVertical, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { fehlerText } from './api';

/* Übungen aus der Übungsbibliothek: nach Muskelgruppe und Kategorie filtern, per Drag & Drop in einen Block des Tages
   ziehen — oder (Tablet, Tastatur) antippen und dann im Block „hier einfügen“ wählen. Eigene Namen gehen über das Feld
   unten („Freie Übung“). */

export default function Bibliothek({ api, gewaehlt, onWahl, darf }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [muskel, setMuskel] = useState('');
  const [kategorie, setKategorie] = useState('');
  const [suche, setSuche] = useState('');
  const [frei, setFrei] = useState('');

  useEffect(() => {
    let aktiv = true;
    const t = setTimeout(() => {
      api.bibliothek({ muskel: muskel || undefined, kategorie: kategorie || undefined, suche: suche || undefined })
        .then((r) => { if (aktiv) { setD(r.data); setErr(''); } })
        .catch((e) => { if (aktiv) setErr(fehlerText(e, 'Bibliothek nicht geladen')); });
    }, 200);
    return () => { aktiv = false; clearTimeout(t); };
  }, [api, muskel, kategorie, suche]);

  const eintraege = useMemo(() => {
    if (!d) return [];
    const kat = Object.fromEntries((d.kategorien || []).map((k) => [k.key, k.label]));
    return [...d.eintraege.map((e) => ({ name: e.name, info: [kat[e.kategorie], (e.muskeln || []).slice(0, 2).join(', ')].filter(Boolean).join(' · ') })),
      ...(d.offen || []).map((name) => ({ name, info: 'im Plan verwendet' }))];
  }, [d]);

  return (
    <div className="space-y-2" data-testid="plan-bibliothek">
      <h2 className="text-base font-bold">Übungsbibliothek</h2>
      <Input aria-label="Übung suchen" placeholder="Übung suchen …" value={suche} onChange={(e) => setSuche(e.target.value)} className="h-11" />
      <div className="grid grid-cols-2 gap-2">
        <select aria-label="Muskelgruppe" value={muskel} onChange={(e) => setMuskel(e.target.value)}
          className="h-11 rounded-md border border-input bg-background px-2 text-sm">
          <option value="">Muskelgruppe</option>
          {(d?.muskeln || []).map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
        <select aria-label="Kategorie" value={kategorie} onChange={(e) => setKategorie(e.target.value)}
          className="h-11 rounded-md border border-input bg-background px-2 text-sm">
          <option value="">Kategorie</option>
          {(d?.kategorien || []).map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
      </div>
      {err && <p className="text-xs text-destructive" role="alert">{err}</p>}
      <Droppable droppableId="bibliothek" isDropDisabled>
        {(prov) => (
          <ul ref={prov.innerRef} {...prov.droppableProps} className="max-h-[50vh] space-y-1 overflow-y-auto pr-1">
            {eintraege.map((e, i) => (
              <Draggable key={e.name} draggableId={`bib:${e.name}`} index={i} isDragDisabled={!darf}>
                {(p, snap) => (
                  <li ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}
                    className={`flex min-h-[44px] items-center gap-2 rounded-[10px] border px-2 py-1.5 text-sm ${snap.isDragging ? 'border-marke bg-card shadow-lg' : gewaehlt === e.name ? 'border-marke bg-marke/10' : 'border-border bg-muted/40'}`}>
                    <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <button type="button" className="min-w-0 flex-1 text-left" onClick={() => onWahl(gewaehlt === e.name ? null : e.name)}
                      aria-pressed={gewaehlt === e.name} disabled={!darf}>
                      <span className="block truncate font-semibold">{e.name}</span>
                      {e.info && <span className="block truncate text-[11px] text-muted-foreground">{e.info}</span>}
                    </button>
                  </li>
                )}
              </Draggable>
            ))}
            {prov.placeholder}
            {d && !eintraege.length && <li className="text-xs text-muted-foreground">Nichts gefunden.</li>}
          </ul>
        )}
      </Droppable>
      {darf && (
        <form className="flex gap-2" onSubmit={(e) => { e.preventDefault(); if (frei.trim()) { onWahl(frei.trim()); setFrei(''); } }}>
          <Input aria-label="Freie Übung" placeholder="Freie Übung" maxLength={80} value={frei} onChange={(e) => setFrei(e.target.value)} className="h-11" />
          <button type="submit" className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-border hover:bg-muted" aria-label="Freie Übung wählen">
            <Plus className="h-4 w-4" />
          </button>
        </form>
      )}
      {gewaehlt && <p className="text-xs text-muted-foreground">„{gewaehlt}“ gewählt — im Block „hier einfügen“ antippen.</p>}
    </div>
  );
}
